<script setup>
import VueApexCharts from 'vue3-apexcharts';

const props = defineProps({
  accessToken: Boolean,
  isMobile: Boolean,
  activeBlur: Boolean,
  mobileWidth: Number,
  paddingRight: String,
  lifeLuckChartData: Object,
})

const emit = defineEmits(['clickChartItem']);
const router = useRouter();

const categoryLabels = ['애정운', '직업운', '학습운', '사회공헌운', '명예운', '인간관계운', '이동운', '건강운', '재물운', '가족운'];
const categoryClassMap = {
  '애정운': 'love',
  '직업운': 'work',
  '학습운': 'study',
  '사회공헌운': 'social',
  '명예운': 'fame',
  '인간관계운': 'relationship',
  '이동운': 'move',
  '건강운': 'health',
  '재물운': 'wealth',
  '가족운': 'family'
};
const seriesData = computed(() => {
  const chartData = props.lifeLuckChartData || {};
  return categoryLabels.map(category => chartData[category] || 0);
});

const series = computed(() => [{ data: seriesData.value }]);

const good = ref({ key: '', value: 0 });
const check = ref({ key: '', value: 0 });

const getGoodAndCheck = (data, labels) => {  
  if (!data || data.length === 0) return {
    good: { key: null, value: null },
    check: { key: null, value: null }
  };

  let maxIndex = 0;
  let minIndex = 0;

  data.forEach((value, index) => {
    if (value > data[maxIndex]) maxIndex = index;
    if (value < data[minIndex]) minIndex = index;
  });

  return {
    good: { key: labels[maxIndex], value: data[maxIndex] },
    check: { key: labels[minIndex], value: data[minIndex] }
  };
};

const chartWidth = ref(300); // 기본값 설정하여 즉시 렌더링
const chartCardRef = ref(null);
let resizeObserver = null;

const calcChartWidth = () => {
  const el = chartCardRef.value;
  if (el && el.clientWidth > 0) {
    const containerWidth = el.clientWidth;
    chartWidth.value = Math.min(containerWidth, 420);
  } else if (window.innerWidth <= (props.mobileWidth || 1080)) {
    chartWidth.value = Math.min(window.innerWidth - 48, 350);
  } else {
    chartWidth.value = 420;
  }
};

// 차트 사이즈 조절
onMounted(() => {
  // 즉시 한 번 계산
  calcChartWidth();
  // DOM이 완전히 렌더링된 후 다시 계산
  nextTick(() => {
    calcChartWidth();
    observeDOMChanges();
  });
});

// 차트 레이블 위치 조절
onMounted(() => {
  setTimeout(() => {
    const labels = document.querySelectorAll('.apexcharts-datalabel');
  
    labels.forEach((label) => {
      const text = label.textContent;
  
      const x = parseFloat(label.getAttribute('x') ?? '0');
      const y = parseFloat(label.getAttribute('y') ?? '0');

      switch (text) {
        case '직업운':
          label.setAttribute('x', x - 2);
          label.setAttribute('y', y - 4);
          break;
        case '학습운':
          label.setAttribute('y', y + 6);
          break;
        case '사회공헌운':
          label.setAttribute('y', y + 10);
          break;
        case '명예운':
          label.setAttribute('x', x - 4);
          label.setAttribute('y', y + 16);
          break;
        case '인간관계운':
          label.setAttribute('y', y + 10);
          break;
        case '이동운':
          label.setAttribute('x', x + 4);
          label.setAttribute('y', y + 16);
          break;
        case '건강운':
          label.setAttribute('y', y + 10);
          break;
        case '재물운':
          label.setAttribute('y', y + 6); 
          break;
        case '가족운':
          label.setAttribute('x', x + 2);
          label.setAttribute('y', y - 4);
          break;
        case '애정운':
        default:
          break;
      }
    });
  }, 200);
});

watch(
  seriesData,
  (newData) => {
    const result = getGoodAndCheck(newData, categoryLabels);  
    good.value = result.good;
    check.value = result.check;
  },
  { immediate: true }
)

const chartTitle = '차트로 보는 오늘의 생활운세';

const radarOptions = computed(() => {
  const radarColors = {
    series1: '#FF6E6E',
    series2: '#5790FC',
  }
  const categoryTextColor = '#757575';
  const chartBorderColor = '#EBEBEB';
  const legendTextColor = '#757575';

  return {
    chart: {
      parentHeightOffset: 0,
      toolbar: { show: false },
      dropShadow: {
        top: 1,
        blur: 8,
        left: 1,
        opacity: 0.2,
        enabled: false,
      },
    },
    markers: {
      size: 4,
      strokeWidth: 2,
      colors: [radarColors.series1, radarColors.series2],
    },
    fill: { opacity: [0.2, 0.2] },
    colors: [radarColors.series1, radarColors.series2],
    stroke: {
      width: 3,
      show: true,
    },
    legend: {
      floating: true,
      labels: {
        colors: legendTextColor,
      },
      markers: {
        offsetX: -4,
        offsetY: 1,
      },
      itemMargin: {
        // vertical: 3,
        // horizontal: 10,
      },
      position: 'right',
      offsetX: -40,
      offsetY: -20
    },
    plotOptions: {
      radar: {
        polygons: {
          strokeColors: chartBorderColor,
          connectorColors: chartBorderColor,
        },
      },
    },
    grid: {
      show: false,
      padding: {
        // top: -20,
        bottom: -40,
      },
    },
    yaxis: {
      show: false,
      tickAmount: 5,
      min: 0,
      max: 100
    },
    xaxis: {
      categories: categoryLabels.map(label => label === '학습운' ? '학업운' : label),
      labels: {
        style: {
          fontSize: '14px',
          fontWeight: 500,
          colors: [
            categoryTextColor,
            categoryTextColor,
            categoryTextColor,
            categoryTextColor,
            categoryTextColor,
            categoryTextColor,
            categoryTextColor,
            categoryTextColor,
            categoryTextColor,
            categoryTextColor,
          ],
        },
      },
    },
    tooltip: {
      enabled: true,
      x: {
        show: false,
        // formatter: function (category) {
        //   return `항목: ${category}`;
        // },
      },
      y: {
        title: {
          formatter: () => {
            return null
          }
        },
        formatter: (val) => {
          // return `${val * 20}%`
          return `${val}%`
        }
      },
      marker: {
        show: false
      },
      theme: 'dark',
      fixed: {
        enabled: true,
        position: 'topRight',
        offsetY: -1,
        offsetX: -100
      }
    }
  }
})

const observeDOMChanges = () => {
  const el = chartCardRef.value;
  if (!el) return;

  resizeObserver = new ResizeObserver(() => {
    nextTick(() => calcChartWidth());
  });
  resizeObserver.observe(el);
};

const clickChartItem = (key) => emit('clickChartItem', key);
</script>

<template>
  <div ref="chartCardRef" class="df-card white">
    <div class="header">📊<br>{{ chartTitle }}</div>

    <div class="chart-card__container">
      <div class="chart-card__item good" @click.stop="clickChartItem(`${good.key}`)">
        <i class="chart-card__icon" :class="categoryClassMap[good.key]"></i>
        <div class="chart-card__text">
          <div class="status">Good</div>
          <div>{{ `${good.key.replace('학습', '학업')} ${good.value}%` }}</div>
        </div>
      </div>

      <div class="chart-card__item check" @click.stop="clickChartItem(`${check.key}`)">
        <i class="chart-card__icon" :class="categoryClassMap[check.key]"></i>
        <div class="chart-card__text">
          <div class="status">Check</div>
          <div>{{ `${check.key.replace('학습', '학업')} ${check.value}%` }}</div>
        </div>
      </div>
    </div>

    <div v-if="chartWidth" class="chart-radar" @click.stop="clickChartItem()">
      <div v-if="isMobile && !accessToken" class="chart-radar__login">
        <div class="chart-radar__login-text">
          로그인 🔒 하면<br>오늘의 운세 🍀 를 무료로 볼 수 있어요!
        </div>
        <button class="chart-radar__login-button" @click="sendMessageToApp({action: 'login'})">
          로그인 하러 가기
        </button>
      </div>
      <div v-if="isMobile && accessToken" class="chart-radar__login">
        <div class="chart-radar__login-text">
          오늘의 운세 🍀 는 내 사주 등록  후,<br>낭만일진에서 무료로 볼 수 있어요!
        </div>
        <button
          class="chart-radar__login-button"
          @click="router.push('/fortune')"
        >
          낭만일진 보러 가기
        </button>
      </div>
      <VueApexCharts
        :key="`chart-${chartWidth}-${seriesData.join('-')}`"
        :class="{ 'chart-radar__blur': activeBlur }"
        type="radar"
        :options="radarOptions"
        :series="series"
        :height="chartWidth"
        :width="chartWidth"
      />
    </div>
  </div>
</template>

<style>
.chart-radar {
  .apexcharts-tooltip {
    border-radius: 30px;
  }

  .apexcharts-tooltip-series-group.apexcharts-active, .apexcharts-tooltip-series-group:last-child {
    padding-bottom: 0 !important;
  }
  
  .apexcharts-tooltip-series-group {
    padding: 0 8px;
  }
}
</style>